import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { computed } from '@angular/core';
import { AuthenticatedUser } from '../../model/user/authenticated-user.type';
import { UserCredentials } from '../../model/user/userCredentials';
import { LocalStorageService } from '../local-storage/local-storage.service';

const HTTP_CONFLICT = 409;

@Injectable({
  providedIn: 'root',
})
export class AuthApiService {
  private readonly _BASE_API_URL = environment.apiUrl;
  private _http: HttpClient = inject(HttpClient);
  private _storage = inject(LocalStorageService);

  user = signal<AuthenticatedUser | null>(null);
  readonly isLoggedIn = computed(() => !!this.user());

  public token(): string | null {
    return this._storage.getToken();
  }

  public getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken');
  }

  public register$(user: UserCredentials): Observable<void> {
    return this._http.post<void>(this._BASE_API_URL + '/auth/register', user).pipe(
      catchError(error => {
        const errorMsg = error.error?.message || 'Erreur lors de l’inscription';
        if (error.status === HTTP_CONFLICT) {
          console.log(error.status === HTTP_CONFLICT);
          return throwError(() => new Error('Cet email est déjà utilisé.'));
        }
        return throwError(() => new Error(errorMsg));
      })
    );
  }

  public autoLogin(): void {
    const token = this.token();
    if (!token) return;

    this.getUser$().subscribe({
      error: err => {
        console.warn('Auto-login échoué :', err.message);
        this.logout$().subscribe();
      },
    });
  }

  public login$(credential: UserCredentials): Observable<AuthenticatedUser | null> {
    return this._http
      .post<{ accessToken: string; refreshToken: string }>(`${this._BASE_API_URL}/auth/login`, credential, { responseType: 'json' })
      .pipe(
        tap(({ accessToken, refreshToken }) => {
          this._storage.setToken(accessToken);
          localStorage.setItem('refreshToken', refreshToken);
        }),
        switchMap(() => this.getUser$()),
        catchError(error => {
          console.error('Erreur lors de la connexion :', error);

          const errorMessage = error.error?.message || 'Erreur de connexion inconnue.';

          return throwError(() => new Error(errorMessage));
        })
      );
  }

  public getUser$(): Observable<AuthenticatedUser | null> {
    const currentUser = this.user();
    if (currentUser) {
      return of(currentUser);
    }
    const token = this.token();
    if (!token) {
      return of(null);
    }

    return this._http.get<AuthenticatedUser>(`${this._BASE_API_URL}/users/me`).pipe(
      tap(result => {
        this.user.set(result);
      }),
      map(() => this.user()),
      catchError(error => {
        console.error('Erreur lors de la récupération de l’utilisateur :', error);
        return throwError(() => new Error(error.error?.message || 'Erreur utilisateur'));
      })
    );
  }

  public refreshTokens$(refreshToken: string): Observable<string> {
    const body = { refreshToken };

    return this._http.post<{ accessToken: string }>(`${this._BASE_API_URL}/auth/refresh`, body).pipe(
      map(response => {
        const newAccessToken = response.accessToken;
        this._storage.setToken(newAccessToken);
        return newAccessToken;
      })
    );
  }

  logout$(): Observable<void> {
    this._storage.removeToken();
    localStorage.removeItem('refreshToken');
    this.user.set(null);
    return of(undefined);
  }

  updateEmail$(newEmail: string): Observable<void> {
    return this._http.patch<void>(`${this._BASE_API_URL}/users/me`, { email: newEmail }).pipe(
      tap(() => this._updateUserPartial({ email: newEmail })),
      catchError(error => {
        console.error('Erreur lors de la mise à jour de l’email:', error);
        return throwError(() => new Error(error.error?.message || 'Erreur lors de la mise à jour de l’email'));
      })
    );
  }

  updatePassword$(newPassword: string): Observable<void> {
    return this._http.patch<void>(`${this._BASE_API_URL}/users/me`, { password: newPassword }).pipe(
      catchError(error => {
        console.error('Erreur lors de la mise à jour du mot de passe:', error);
        return throwError(() => new Error(error.error?.message || 'Erreur lors de la mise à jour du mot de passe'));
      })
    );
  }

  deleteAccount$(): Observable<void> {
    const userId = this.user()?.id;

    if (!userId) {
      return throwError(() => new Error('ID utilisateur introuvable'));
    }

    return this._http.delete<void>(`${this._BASE_API_URL}/users/me`).pipe(
      tap(() => {
        this._storage.removeToken();
        this.user.set(null);
      }),
      catchError(error => {
        console.error('Erreur lors de la suppression du compte:', error);
        return throwError(() => new Error(error.error?.message || 'Erreur lors de la suppression du compte'));
      })
    );
  }

  private _updateUserPartial(changes: Partial<AuthenticatedUser>): void {
    const currentUser = this.user();
    if (currentUser) {
      this.user.set({ ...currentUser, ...changes });
    }
  }

  /**
   * Envoie une requête au backend pour demander un token de réinitialisation.
   * @param email L'adresse e-mail de l'utilisateur.
   */
  public requestPasswordReset$(email: string): Observable<void> {
    return this._http.post<void>(`${this._BASE_API_URL}/users/forgot-password?email=${email}`, null).pipe(
      catchError(error => {
        const errorMsg = error.error?.message || 'Erreur lors de la demande de réinitialisation.';
        return throwError(() => new Error(errorMsg));
      })
    );
  }

  /**
   * Envoie le token et le nouveau mot de passe pour la réinitialisation.
   * @param token Le token reçu par e-mail.
   * @param newPassword Le nouveau mot de passe.
   */
  public resetPassword$(token: string, newPassword: string): Observable<void> {
    const body = { token, newPassword };
    return this._http.post<void>(`${this._BASE_API_URL}/users/reset-password`, body).pipe(
      catchError(error => {
        const errorMsg = error.error?.message || 'Erreur lors de la réinitialisation du mot de passe.';
        return throwError(() => new Error(errorMsg));
      })
    );
  }
}
